import React, { useState } from "react";
import useReportListLogic, { Blacklist } from "./HostBlacklist.logic";
import { DataTable, DataTableFilterMeta } from "primereact/datatable";
import { Column } from "primereact/column";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { FilterMatchMode } from "primereact/api";
import { confirmPopup, ConfirmPopup } from "primereact/confirmpopup";
import { formatDate } from "../../../utils/dateUtils";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { RadioButton } from "primereact/radiobutton";
import { InputTextarea } from "primereact/inputtextarea";
import { MultiSelect } from "primereact/multiselect";
import classNames from "classnames";

const HostBlackList: React.FC = () => {
  const {
    blacklists,
    loading,
    blacklistLoading,
    submitted,
    showBlacklistDialog,
    showDialog,
    hideDialog,
    blacklistForm,
    onInputChange,
    handleBlacklistEvent,
    setBlacklistForm,
    hosts,
    handleRevokeBlackEvent
  } = useReportListLogic();

  const revokeConfirm = (event: any, id: number | null) => {
    if (id == null) {
      return;
    }
    confirmPopup({
      target: event.currentTarget,
      message: "Do you want to revoke this blacklist?",
      icon: "pi pi-info-circle",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      accept: () => {
        handleRevokeBlackEvent(id);
      },
    });
  };

  const statusBodyTemplate = (rowData: Blacklist) => {
    return rowData.is_active == true ? (
      <span className="p-tag p-tag-danger">{rowData.is_active_label}</span>
    ) : (
      <span className="p-tag p-tag-info">{rowData.is_active_label}</span>
    );
  };

  const [globalFilterValue, setGlobalFilterValue] = useState<string>("");

  const [filters, setFilters] = useState<DataTableFilterMeta>({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
  });

  const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    let _filters = { ...filters };

    // @ts-ignore
    _filters["global"].value = value;

    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const renderHeader = () => {
    return (
      <>
        <ConfirmPopup />
        <div className="flex justify-content-start align-items-center mb-2 flex-column-responsive">
          <IconField iconPosition="left">
            <InputIcon className="fas fa-search" />
            <InputText
              value={globalFilterValue}
              onChange={onGlobalFilterChange}
              placeholder="Keyword Search"
            />
          </IconField>
          <Button
            label="Add Host to Blacklist"
            onClick={() => showDialog()}
            icon="pi pi-plus"
            className="alt_btn ml-auto"
          />
        </div>
      </>
    );
  };

  const header = renderHeader();

  const dialogFooter = (
    <React.Fragment>
      <div className="col-xl-12 d-flex align-items-center justify-content-center">
        <Button
          label="Cancel"
          icon="pi pi-times"
          outlined
          onClick={hideDialog}
          disabled={blacklistLoading}
          className="thm-black-btn small_btn"
        />
        <Button
          label={blacklistLoading ? "Submitting..." : "Submit Action"}
          icon={blacklistLoading ? "pi pi-spin pi-spinner" : "pi pi-check"}
          onClick={handleBlacklistEvent}
          disabled={blacklistLoading}
          className="thm-btn small_btn"
        />
      </div>
    </React.Fragment>
  );

  const actionBodyTemplate = (rowData: Blacklist) => {
    return (
      <>
        {rowData.is_active === true && (
          <Button
            icon={"fas fa-undo"}
            rounded
            tooltip={"Revoke Blacklist Status"}
            tooltipOptions={{ position: "top" }}
            onClick={(event) => revokeConfirm(event, rowData.id)}
          ></Button>
        )}
      </>
    );
  };

  return (
    <>
      <section className="schedule-three ngo_list_wrapper">
        <div className="container">
          <div className="schedule-three__top">
            <div
              className="section-title text-center wow fadeInLeft"
              data-wow-delay="100ms"
            >
              <h2 className="section-title__title">Blacklisted Hosts</h2>
            </div>
          </div>
          <div className="schedule-three__bottom">
            <DataTable
              value={blacklists}
              removableSort
              sortField="blacklisted_at"
              sortOrder={-1}
              showGridlines
              paginator
              rows={5}
              loading={loading}
              rowsPerPageOptions={[5, 10, 25, 50]}
              filters={filters}
              // filterDisplay="row"
              globalFilterFields={[
                "blacklistedHost.name",
                "blacklistedBy.name",
                "revokedBy.name",
                "reason",
                "is_active_label",
              ]}
              header={header}
              emptyMessage="No blacklisted hosts found."
              onFilter={(e) => setFilters(e.filters)}
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
              currentPageReportTemplate="Showing {first} to {last} of {totalRecords} Blacklisted Hosts"
              tableStyle={{ minWidth: "50rem" }}
            >
              <Column
                field="blacklistedHost.name"
                header="Host Name"
                sortable
              ></Column>
              <Column
                field="blacklistedHost.email"
                header="Host Email"
                sortable
              ></Column>
              <Column
                field="reason"
                header="Reason for Blacklisting"
                sortable
              ></Column>
              <Column
                headerStyle={{ width: "10%", minWidth: "8rem" }}
                field="is_active_label"
                header="Status"
                bodyStyle={{ textAlign: "center" }}
                body={statusBodyTemplate}
                headerClassName="custom-status-column"
              ></Column>
              <Column
                field="blacklisted_at"
                header="Reported At"
                body={(rowData: Blacklist) => rowData.blacklisted_at_formatted}
                sortable
              ></Column>
              <Column
                field="blacklistedBy.name"
                header="Blacklisted By"
                sortable
              ></Column>
              <Column
                headerStyle={{ width: "15%", minWidth: "8rem" }}
                bodyStyle={{ textAlign: "center" }}
                headerClassName="custom-status-column"
                header="Actions"
                body={actionBodyTemplate}
              ></Column>
            </DataTable>
          </div>
        </div>
      </section>
      <Dialog
        visible={showBlacklistDialog}
        style={{ width: "32rem" }}
        breakpoints={{ "960px": "75vw", "641px": "90vw" }}
        header={"Update Report Status"}
        modal
        className="p-fluid"
        footer={dialogFooter}
        onHide={hideDialog}
        draggable={false}
      >
        <div className="field mb-3">
          <label htmlFor="reason" className="font-bold">
            Reason for Report
          </label>
          <MultiSelect
            value={blacklistForm.host_ids}
            options={hosts}
            placeholder="Select host"
            onChange={(e) =>
              setBlacklistForm({ ...blacklistForm, host_ids: e.value })
            }
            optionLabel="name"
            optionValue="id"
            maxSelectedLabels={3}
            display="comma"
            className={classNames("w-full", {
              "p-invalid": submitted && blacklistForm.host_ids.length === 0,
            })}
            itemTemplate={(option) => (
              <div className="flex flex-col">
                <span>{option.name}</span>
                <span className="text-sm text-gray-500 ms-2">
                  ({option.email})
                </span>
              </div>
            )}
          />

          {submitted && !blacklistForm.host_ids.length && (
            <small className="p-error">Host is required.</small>
          )}
        </div>

        <div className="field">
          <label htmlFor="reason" className="font-bold">
            Reason
          </label>
          <InputTextarea
            id="reason"
            value={blacklistForm.reason}
            onChange={(e) => onInputChange(e, "reason")}
            placeholder="Please provide details about your action."
            rows={3}
            autoResize
          />
          {submitted && !blacklistForm.reason && (
            <small className="p-error">Reason is required.</small>
          )}
        </div>
      </Dialog>
    </>
  );
};

export default HostBlackList;
